/**
 * ProjectMembershipService - Service class for project membership operations
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles project membership operations
 * - Open/Closed: Can be extended without modifying existing code
 * - Liskov Substitution: Can be substituted with other membership services
 * - Interface Segregation: Focused interface for project membership only
 * - Dependency Inversion: Depends on abstractions (apiClient) not concretions
 */
class ProjectMembershipService {
    constructor(apiClient) {
        this.apiClient = apiClient
    }

    /**
     * Get projects for a specific user with association details
     * @param {string} userId - User ID
     * @returns {Promise<Object>} API response with user projects
     */
    async getUserProjects(userId) {
        if (!userId) {
            throw new Error('User ID is required')
        }

        try {
            const response = await this.apiClient.getUserProjects(userId)
            return {
                success: true,
                data: response,
                projects: response.projects || []
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to load user projects',
                projects: []
            }
        }
    }

    /**
     * Remove direct user access from a project
     * @param {string} projectId - Project ID
     * @param {string} userId - User ID
     * @returns {Promise<Object>} API response
     */
    async removeUserFromProject(projectId, userId) {
        if (!projectId || !userId) {
            throw new Error('Project ID and User ID are required')
        }

        try {
            const response = await this.apiClient.removeUserFromProject(projectId, userId)
            return {
                success: true,
                data: response,
                message: response.message || 'User removed from project successfully'
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to remove user from project'
            }
        }
    }

    /**
     * Process project data to include association type information
     * @param {Array} projects - Raw project data
     * @returns {Array} Processed projects with association details
     */
    processProjectsWithAssociations(projects) {
        if (!Array.isArray(projects)) {
            return []
        }

        return projects.map(project => ({
            ...project,
            direct_access: project.direct_access || false,
            group_accesses: project.group_accesses || [],
            role: project.role || 'member'
        }))
    }

    /**
     * Check if user has direct access to a project
     * @param {Object} project - Project object
     * @returns {boolean} True if user has direct access
     */
    hasDirectAccess(project) {
        return project?.direct_access === true
    }

    /**
     * Get group access details for a project
     * @param {Object} project - Project object
     * @returns {Array} Array of group access details
     */
    getGroupAccesses(project) {
        return project?.group_accesses || []
    }

    /**
     * Create confirmation message for removing user from project
     * @param {Object} user - User object
     * @param {Object} project - Project object
     * @returns {string} Confirmation message
     */
    createRemoveConfirmationMessage(user, project) {
        const userName = user?.full_name || user?.display_name || user?.email || 'this user'
        const projectName = project?.name || 'the project'
        
        return `Are you sure you want to remove direct access for ${userName} from ${projectName}?`
    }

    /**
     * Format association type text for display
     * @param {Object} project - Project object
     * @returns {string} Formatted association type
     */
    formatAssociationType(project) {
        const associations = []
        
        if (this.hasDirectAccess(project)) {
            associations.push('Direct')
        }
        
        const groupAccesses = this.getGroupAccesses(project)
        if (groupAccesses.length > 0) {
            const groupNames = groupAccesses.map(access => access.group_name).join(', ')
            associations.push(`Via Groups: ${groupNames}`)
        }
        
        return associations.length > 0 ? associations.join(' • ') : 'No access'
    }
}

export default ProjectMembershipService